/**
 * ValidationChecklist.js — Validation results with category-level checks.
 *
 * Uses validateComposedMechanic from the engine schema to show
 * green checks / red X-circles per category, plus specific error messages.
 *
 * Part of the Homebrew Workshop (Issue #82, Phase E: #180)
 */

import React from 'react';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { validateComposedMechanic } from '../../../engine/schema';

export default function ValidationChecklist({ config }) {
  const { valid, errors } = validateComposedMechanic(config);

  // Group errors by category based on error message prefixes
  const categories = [
    {
      label: 'Basics',
      check: () => !errors.some(e =>
        e.startsWith('meta') || e.startsWith('id') || e.startsWith('version')
      ),
    },
    {
      label: 'Actions',
      check: () => !errors.some(e =>
        e.startsWith('actions') || e.includes('action') || e.includes('Must have at least')
      ),
    },
    {
      label: 'Tokens',
      check: () => !errors.some(e => e.startsWith('tokens')),
    },
    {
      label: 'Display',
      check: () => !errors.some(e => e.startsWith('display')),
    },
    {
      label: 'Overall',
      check: () => valid,
    },
  ];

  return (
    <div className="mt-4">
      <h3 className="text-sm font-bold text-gray-400 uppercase mb-2">Validation</h3>
      <div className="space-y-1">
        {categories.map(cat => {
          const passed = cat.check();
          return (
            <div key={cat.label} className="flex items-center gap-2 text-xs">
              {passed ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" />
              ) : (
                <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
              )}
              <span className={passed ? 'text-green-400' : 'text-red-400'}>
                {cat.label} {passed ? 'valid' : 'has issues'}
              </span>
            </div>
          );
        })}

        {/* Show specific errors */}
        {errors.length > 0 && (
          <div className="mt-2 pl-5 space-y-0.5">
            {errors.slice(0, 5).map((err, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs text-red-400/80">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{err}</span>
              </div>
            ))}
            {errors.length > 5 && (
              <span className="text-xs text-gray-500">+{errors.length - 5} more</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
